import { Box, Typography } from '@mui/material';
import { EditOutlined, VisibilityOutlined } from '@mui/icons-material';

import {
  FBoxContainer,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHSkeletonLoader,
} from '../../../../components/base';
import { Roles } from '../../../../constants';
import { areUserRolesIn } from '../../../../helpers';
import { FORTA_ERROR } from '../../../../utils/colors';
import {
  PROVIDER_SSN_ALLOWED_ROLES,
  PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH,
} from '../constants';
import { useProviderDetailsState, useProviderSsn } from '../hooks';
import { PROVIDER_SSN_NO_HELPER_INPUT_SX } from '../styles';
import { formatProviderSsnInput } from '../utils';

import type { ChangeEvent } from 'react';

export function ProviderSsn() {
  const { provider, user } = useProviderDetailsState();
  const canManageSsn = areUserRolesIn(user?.roles, PROVIDER_SSN_ALLOWED_ROLES);
  const isAdmin = areUserRolesIn(user?.roles, [Roles.Admin]);
  const {
    maskedSsn,
    ssnLoaded,
    loading,
    submitting,
    editOpen,
    editValue,
    setEditValue,
    revealOpen,
    revealPassword,
    setRevealPassword,
    revealedSsn,
    error,
    openEdit,
    closeEdit,
    save,
    openReveal,
    closeReveal,
    reveal,
  } = useProviderSsn(canManageSsn ? provider?.id : undefined);
  const revealPasswordTooShort =
    revealPassword.length > 0 &&
    revealPassword.length < PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH;
  const revealErrorMessage = revealPasswordTooShort
    ? 'Password must be at least eight characters'
    : error;
  const canEditSsn = ssnLoaded && (isAdmin || !maskedSsn);
  const editButtonLabel = maskedSsn ? 'Edit' : 'Add';

  if (!canManageSsn) return null;

  const handleEditChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEditValue(formatProviderSsnInput(event.target.value));
  };
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRevealPassword(event.target.value);
  };
  const renderError = (message = error) =>
    message ? <Typography color={FORTA_ERROR}>{message}</Typography> : null;
  const renderEditContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 3 }}>
      <FHInputField
        autoComplete="off"
        aria-label="SSN"
        inputMode="numeric"
        inputProps={{ maxLength: 11 }}
        placeholder="SSN"
        type="text"
        value={editValue}
        onChange={handleEditChange}
      />
      {renderError()}
    </Box>
  );
  const renderRevealContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 3 }}>
      {revealedSsn ? (
        <Typography variant="h5">{formatProviderSsnInput(revealedSsn)}</Typography>
      ) : (
        <FHInputField
          autoComplete="off"
          aria-label="Password"
          error={Boolean(revealErrorMessage)}
          inputProps={{ minLength: PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH }}
          placeholder="Password"
          sx={PROVIDER_SSN_NO_HELPER_INPUT_SX}
          type="password"
          value={revealPassword}
          onChange={handlePasswordChange}
        />
      )}
      {renderError(revealErrorMessage)}
    </Box>
  );

  return (
    <FBoxContainer>
      <Typography variant="h6" mb={2}>
        Social Security Number
      </Typography>
      {loading ? (
        <FHSkeletonLoader height={40} width={260} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FHInputField
            disabled
            value={maskedSsn ?? ''}
            aria-label="Provider SSN"
            sx={PROVIDER_SSN_NO_HELPER_INPUT_SX}
          />
          {canEditSsn && (
            <FHButton startIcon={<EditOutlined />} onClick={openEdit}>
              {editButtonLabel}
            </FHButton>
          )}
          {canManageSsn && maskedSsn && (
            <FHButton startIcon={<VisibilityOutlined />} onClick={openReveal}>
              Reveal
            </FHButton>
          )}
        </Box>
      )}
      {!editOpen && !revealOpen && renderError()}
      <FHConfirmDialog
        open={editOpen}
        title="Update SSN"
        submittext="Save"
        canceltext="Cancel"
        hideCloseIcon
        submitLoading={submitting}
        disableSubmitButton={!editValue.trim()}
        contentRenderer={renderEditContent}
        onClose={closeEdit}
        onSubmit={save}
      />
      <FHConfirmDialog
        open={revealOpen}
        title="Reveal SSN"
        submittext={revealedSsn ? undefined : 'Reveal'}
        canceltext="Close"
        hideCloseIcon
        submitLoading={submitting}
        hideSubmitButton={Boolean(revealedSsn)}
        disableSubmitButton={!revealPassword || revealPasswordTooShort}
        dialogHeight="auto"
        contentRenderer={renderRevealContent}
        onClose={closeReveal}
        onSubmit={reveal}
      />
    </FBoxContainer>
  );
}
